import { prepareTexts, storeOriginalTexts } from './split'
import {
	getEasedStaggerDelayForTransitionOutAlt,
	getTextAnimationEasingForTarget,
	getTextAnimationStaggerDelay,
	getTextAnimationStaggerEasingForTarget,
	getMaskedTextRevealStartY,
	setLineTextMaskRevealClip,
} from './util'
import { getWordLevelAnimationTargets, getCharacterLevelAnimationTargets } from './text_animation_targets'
import {
	clearTypewriterBackgroundReveal,
	prepareTypewriterBackgroundReveal,
	revealTypewriterUnitBackground,
} from './typewriter_background_reveal'
import {
	applyPreRevealVisualStateToTarget,
	discardTextAnimationTypewriterTimeouts,
	isTextAnimationSplitDomReadyForPlay,
	reapplyViewportAfterTextDomResplit,
	snapTextAnimationToFinishedAppearance,
	taResizeTrace,
	taResizeTraceTarget,
	taSplitLog,
	taSplitLogTarget,
} from './text_animation_resplit_recovery'
import animation_lifecycle_controller from '../animations/lifecycle/animation_lifecycle_controller'
// Store animation timelines in a WeakMap
const animationTimelines = new WeakMap()
// Store timeout IDs for typewriter animations
const typewriterTimeouts = new WeakMap()

// Store targets globally
let currentTargets = []
let column_selectors = ''

const isIOS = () => {
	return typeof document !== 'undefined' && document.documentElement.classList.contains('ios')
}

const normalizeReEntryDirection = (value) => {
	return value === 'from_above' || value === 'both' || value === 'from_below' ? value : 'from_below'
}

const reEntryAllows = (mode, side) => {
	return mode === side || mode === 'both'
}

// Scrub-mode targets are driven entirely by `text_animation_scrub.js` (manual
// timeline.seek per scroll tick). The IO-driven playAnimation path here must
// not touch them — `timeline.play()` would override the paused-and-seek state
// and the IO classes (text-animation-in-view etc.) are meaningless for scrub.
const isScrubTarget = (target) => {
	return !!(target && target.getAttribute && target.getAttribute('data-animation-scrub') === 'true')
}

const resumeInViewTextAnimationsIfNeeded = (targets) => {
	if (!isIOS()) {
		return
	}
	targets.forEach((target) => {
		if (!target || !target.classList.contains('text-animation-in-view')) {
			return
		}
		// Scrub mode owns its own play state via timeline.seek per scroll
		// tick; .play() would clobber that.
		if (isScrubTarget(target)) {
			return
		}
		const timeline = animationTimelines.get(target)
		if (!timeline || timeline.completed) {
			return
		}
		const progress = typeof timeline.progress === 'number' ? timeline.progress : 0
		if (progress <= 0 || progress >= 100) {
			return
		}
		if (timeline.paused) {
			timeline.play()
		}
	})
}

// Configuration for text splitting
let selector = '.lay-text-animation'

window.laytheme.on('texts_are_split', function (detail) {
	// Union iteration is intentional here — we filter by mutation-set
	// below to handle only the targets whose DOM was actually re-split.
	let targets = getAllScopeTargets()
	const fromResize = !!(detail && detail.fromResize)
	const resizeTargets = detail && detail.resizeMutationTargets
	const firstSplitTargets = detail && detail.firstSplitMutationTargets

	taSplitLog('texts_are_split (grid) handler', {
		targetCount: targets.length,
		fromResize,
		resizeMutationTargetCount: resizeTargets ? resizeTargets.size : 0,
		firstSplitMutationTargetCount: firstSplitTargets ? firstSplitTargets.size : 0,
	})
	taResizeTrace('texts_are_split handler (grid) enter', {
		targetCount: targets.length,
		fromResize,
		resizeMutationTargetCount: resizeTargets ? resizeTargets.size : 0,
	})

	targets.forEach((target) => {
		// Scrub targets have their own `texts_are_split` handler in scrub.js
		// that rebuilds pairings (and therefore the timeline). Skip the
		// IO-mode bookkeeping here entirely — the scrub controller owns these.
		if (isScrubTarget(target)) {
			return
		}
		const inResizeMutationSet = !!(resizeTargets && resizeTargets.has(target))
		const inFirstSplitMutationSet = !!(firstSplitTargets && firstSplitTargets.has(target))
		// Direct-load fires content_ready twice (overlay + lazy underlay). The
		// underlay's prepareTexts emits texts_are_split for ITS targets, not
		// the overlay's — but this listener iterates the union. Without this
		// guard, the overlay's text_15 (mid-play timeline from NAV_SYNC) would
		// land in the in-progress branch below and get reset+replayed →
		// "first text plays twice". When mutation-set tracking is present
		// (post-fix), only proceed for targets whose DOM was actually touched.
		const detailHasMutationTracking = !!(resizeTargets || firstSplitTargets)
		if (detailHasMutationTracking && !inResizeMutationSet && !inFirstSplitMutationSet) {
			return
		}
		const fromResizeForTarget =
			inResizeMutationSet || (!resizeTargets || resizeTargets.size === 0 ? fromResize : false)

		const oldTimeline = animationTimelines.get(target)

		taResizeTraceTarget(target, 'texts_are_split target', {
			inResizeMutationSet,
			fromResizeForTarget,
			globalFromResize: fromResize,
			hadTimeline: !!oldTimeline,
			timelineProgress: oldTimeline && typeof oldTimeline.progress === 'number' ? oldTimeline.progress : null,
			timelineCompleted: oldTimeline ? !!oldTimeline.completed : null,
		})

		if (fromResizeForTarget) {
			if (oldTimeline) {
				oldTimeline.pause()
				animationTimelines.delete(target)
				discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target)
			}
			target._layPendingPlayAfterSplit = false
			const wasPlayedBefore =
				!!oldTimeline ||
				target.classList.contains('lay-text-animation-initialized') ||
				target.classList.contains('text-animation-in-view')
			if (wasPlayedBefore) {
				taResizeTraceTarget(target, 'texts_are_split → resize path: SNAP finished', {
					hadTimeline: !!oldTimeline,
					initialized: target.classList.contains('lay-text-animation-initialized'),
				})
				taSplitLogTarget(target, 'texts_are_split: resize — snap finished', {
					hadTimeline: !!oldTimeline,
					initialized: target.classList.contains('lay-text-animation-initialized'),
				})
				snapTextAnimationToFinishedAppearance(target)
				target.classList.remove('text-animation-never-played')
				target.classList.remove('text-animation-ready-to-play')
				target.classList.add('text-animation-in-view')
			} else {
				taResizeTraceTarget(target, 'texts_are_split → resize path: skip snap (never played)', {})
				taSplitLogTarget(target, 'texts_are_split: resize — skip snap (never played yet)')
			}
			return
		}

		if (!oldTimeline) {
			taResizeTraceTarget(target, 'texts_are_split → non-resize: skip (no timeline)', {
				inResizeMutationSet,
				fromResizeForTarget,
			})
			taSplitLogTarget(target, 'texts_are_split: skip — no timeline on this target')
			return
		}

		oldTimeline.pause()
		animationTimelines.delete(target)
		discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target)

		const progress = typeof oldTimeline.progress === 'number' ? oldTimeline.progress : 0
		const wasCompleted = !!(oldTimeline.completed || progress >= 99.5)

		taSplitLogTarget(target, 'texts_are_split: had timeline', {
			progress,
			completed: oldTimeline.completed,
			wasCompleted,
			fromResize: fromResizeForTarget,
		})

		if (wasCompleted) {
			taResizeTraceTarget(target, 'texts_are_split → non-resize: SNAP (was completed)', { progress })
			snapTextAnimationToFinishedAppearance(target)
			target.classList.remove('text-animation-never-played')
			target.classList.remove('text-animation-ready-to-play')
			target.classList.add('text-animation-in-view')
		} else {
			taResizeTraceTarget(target, 'texts_are_split → non-resize: REPLAY (in-progress)', {
				progress,
				completed: oldTimeline.completed,
			})
			taSplitLogTarget(target, 'texts_are_split: in-progress → reset + reapplyViewport')
			resetAnimationState(target)
			reapplyViewportAfterTextDomResplit(target, playAnimation)
		}
	})
})

// Retry handler for targets whose `playAnimation` was bailed because the
// split DOM wasn't ready yet (initObserver fires the in-viewport play
// synchronously, but `prepareTexts` runs async — there's a race where
// `.lay-text-line` / `.char` don't exist when the IO call lands).
// Subscribed AFTER the main `texts_are_split` handler so the split DOM is
// definitely in place by the time we re-attempt.
window.laytheme.on('texts_are_split', function () {
	currentTargets.forEach((target) => {
		if (isScrubTarget(target)) return
		if (!target || !target._layPendingPlayAfterSplit) return
		if (!isTextAnimationSplitDomReadyForPlay(target)) return
		target._layPendingPlayAfterSplit = false
		playAnimation(target)
	})
})

// Function to set up intersection observer for all text elements
const initObserver = (targetsForThisScope) => {
	// Default to global currentTargets for backward compatibility — but the
	// content_ready handler now passes a per-scope list so we observe only
	// THIS scope's targets, not the union. (Without this, the second
	// content_ready in direct-load mode would re-observe overlay targets
	// inside the underlay's initObserver call, doubling subscriptions.)
	const targets = Array.isArray(targetsForThisScope) ? targetsForThisScope : currentTargets
	taSplitLog('initObserver (grid) start', { targetCount: targets.length })
	// Create a map to store observers for each target
	const observers = new Map()
	const resetObservers = new Map()
	let resumeTimeoutId = null
	const scheduleIOSResumeCheck = () => {
		if (!isIOS()) {
			return
		}
		if (resumeTimeoutId) {
			window.clearTimeout(resumeTimeoutId)
		}
		resumeTimeoutId = window.setTimeout(() => {
			// Resume across all targets (union); both scopes' visible
			// elements are eligible for the iOS resume nudge.
			resumeInViewTextAnimationsIfNeeded(currentTargets)
		}, 80)
	}

	// Set up individual intersection observer for each target
	targets.forEach((target) => {
		// Scrub targets are driven by scroll progress (see scrub.js), not by
		// the IO viewport-entry trigger. Skip observer setup entirely.
		if (isScrubTarget(target)) {
			return
		}
		// Get the data-top value or use default 80%
		const dataTop = parseFloat(target.getAttribute('data-top')) || 80
		let top = 100 - parseInt(dataTop)
		// Create negative rootMargin to trigger when viewport is dataTop% away from element
		const rootMargin = `0px 0px -${top}% 0px`

		// Check initial position
		const rect = target.getBoundingClientRect()
		const wasAlreadyRevealed =
			target.classList.contains('text-animation-in-view') ||
			target.classList.contains('lay-text-animation-initialized')
		// Direct-load: NAV_SYNC_START_EVENT fires inside `texts_are_split`
		// (via the bootstrap's emitNavCompleted), which calls playAnimation
		// for nav-synced targets — setting `lay-text-animation-initialized`
		// before initObserver runs. Without the in-progress check below,
		// `snapTextAnimationToFinishedAppearance` would jump the still-playing
		// timeline to end-state, the timeline would then continue from where
		// it was and the user sees a visible "snap → animate from start"
		// double-play.
		const existingTimeline = animationTimelines.get(target)
		const animationCurrentlyPlaying = !!(
			existingTimeline &&
			!existingTimeline.completed &&
			typeof existingTimeline.progress === 'number' &&
			existingTimeline.progress < 100
		)
		if (wasAlreadyRevealed && rect.top <= window.innerHeight && !animationCurrentlyPlaying) {
			snapTextAnimationToFinishedAppearance(target)
			target.classList.remove('text-animation-never-played')
			target.classList.remove('text-animation-ready-to-play')
			if (rect.top >= 0) {
				target.classList.add('text-animation-in-view')
			} else {
				target.classList.remove('text-animation-in-view')
			}
		} else if (!wasAlreadyRevealed) {
			// Mark as never played initially
			target.classList.add('text-animation-never-played')
		}

		if (rect.top > window.innerHeight) {
			// Element is below viewport - mark as ready to animate when scrolling down
			target.classList.add('text-animation-ready-to-play')
		} else if (rect.top >= 0 && !wasAlreadyRevealed) {
			// Element is in viewport (including at the very top where rect.top = 0)
			playAnimation(target)
		}
		// If element is above viewport (else case), don't add any ready class

		// Observer for triggering animations
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const timeline = animationTimelines.get(entry.target)
						const progress = timeline && typeof timeline.progress === 'number' ? timeline.progress : 0
						if (timeline && timeline.paused && !timeline.completed && progress > 0 && progress < 100) {
							if (waitForBuildAnimGroupTriggerIfNeeded(entry.target)) {
								return
							}
							timeline.play()
							entry.target.classList.add('text-animation-in-view')
							return
						}
						// Check if scrolling from below (down scroll) or never played before
						const comingFromBelow = entry.boundingClientRect.top > 0
						const neverPlayed = entry.target.classList.contains('text-animation-never-played')
						const readyToPlay = entry.target.classList.contains('text-animation-ready-to-play')
						const readyFrom = entry.target.getAttribute('data-text-animation-ready-from') || 'from_below'
						const isAllowedReEntry = readyFrom === 'both' || readyFrom === (comingFromBelow ? 'from_below' : 'from_above')

						// Play if: (coming from below AND ready) OR never played before
						if ((readyToPlay && isAllowedReEntry) || neverPlayed) {
							playAnimation(entry.target)
						}
					}
				})
			},
			{
				threshold: 0,
				rootMargin: rootMargin,
			}
		)

		// Observer for detecting when element is completely out of view
		const resetObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						const isBelow = entry.boundingClientRect.top > window.innerHeight
						const isAbove = entry.boundingClientRect.bottom < 0
						const reEntryMode = normalizeReEntryDirection(entry.target.getAttribute('data-repeat-on-re-enter-direction'))

						if (isBelow) {
							// iOS: let animation run to completion if it has started (avoids interrupt while finger is down)
							const timeline = animationTimelines.get(entry.target)
							const inProgress = timeline && !timeline.completed && timeline.progress > 0
							if (isIOS() && inProgress) {
								return
							}
							// "Animate again on re-entry" off — skip the
							// reset-and-rearm so the text stays in its
							// finished state forever. PHP emits
							// `data-repeat-on-re-enter="0"` only when
							// explicitly opted out; missing attr =
							// default replay.
							if (entry.target.getAttribute('data-repeat-on-re-enter') === '0' || !reEntryAllows(reEntryMode, 'from_below')) {
								return
							}
							// Element went below viewport - reset and mark as ready to play again
							resetAnimationState(entry.target)
							entry.target.classList.add('text-animation-ready-to-play')
							entry.target.setAttribute('data-text-animation-ready-from', 'from_below')
							entry.target.classList.remove('text-animation-in-view')
						} else if (isAbove) {
							if (entry.target.getAttribute('data-repeat-on-re-enter') !== '0' && reEntryAllows(reEntryMode, 'from_above')) {
								resetAnimationState(entry.target)
								entry.target.classList.add('text-animation-ready-to-play')
								entry.target.setAttribute('data-text-animation-ready-from', 'from_above')
								entry.target.classList.remove('text-animation-in-view')
								return
							}
							// Element went above viewport - complete animation to finished state
							// This ensures text is fully visible even if animation was interrupted
							const timeline = animationTimelines.get(entry.target)
							if (timeline) {
								timeline.seek(timeline.duration) // Jump to end
							}
							entry.target.classList.remove('text-animation-ready-to-play')
							entry.target.classList.remove('text-animation-in-view')
						}
					}
				})
			},
			{
				threshold: 0,
				rootMargin: '0px',
			}
		)

		// Store observers and start observing
		observers.set(target, observer)
		resetObservers.set(target, resetObserver)
		observer.observe(target)
		resetObserver.observe(target)
	})

	window.addEventListener('scroll', scheduleIOSResumeCheck, { passive: true })
	window.addEventListener('touchend', scheduleIOSResumeCheck, { passive: true })
	window.addEventListener('touchcancel', scheduleIOSResumeCheck, { passive: true })

	// Return cleanup function
	return () => {
		if (resumeTimeoutId) {
			window.clearTimeout(resumeTimeoutId)
		}
		window.removeEventListener('scroll', scheduleIOSResumeCheck)
		window.removeEventListener('touchend', scheduleIOSResumeCheck)
		window.removeEventListener('touchcancel', scheduleIOSResumeCheck)
		observers.forEach((observer) => observer.disconnect())
		resetObservers.forEach((observer) => observer.disconnect())
		observers.clear()
		resetObservers.clear()
	}
}

// Function to reset animation state
const resetAnimationState = (target) => {
	// Get the timeline if it exists
	const timeline = animationTimelines.get(target)
	if (timeline) {
		// Reset the timeline to its initial state
		timeline.pause()
		timeline.seek(0)
	}

	const animationType = target.getAttribute('data-animation-type')
	if (animationType === 'typewriter' || animationType === 'typewriter_words' || animationType === 'typewriter_lines') {
		const existingTimeouts = typewriterTimeouts.get(target)
		if (existingTimeouts) {
			existingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
			typewriterTimeouts.delete(target)
		}
		clearTypewriterBackgroundReveal(target)
	}

	applyPreRevealVisualStateToTarget(target)
	taSplitLogTarget(target, 'resetAnimationState done (via applyPreReveal)')
}

// Build Animation Group auto-fit: when an element is a group member,
// `data-build-anim-total-duration` carries the slot's target total ms.
// We scale the element's per-unit duration & stagger so that
//   (itemCount - 1) * stagger + duration ≈ targetTotal
// — i.e. a 5-word masked_words text and a 30-char masked_characters
// text finish inside the same slot even though their sub-item counts
// differ wildly. Returns null when the element is not in a group, so
// the caller can fall back to the user's literal per-unit values.
//
// Scale is clamped to [0.05, 10] so a wildly mismatched slot doesn't
// turn into invisible-fast or freeze-slow animations. The clamp will
// silently break exact fit in extreme cases, which is fine — the user
// can detach and re-tune if it matters.
function computeBuildAnimScaling(target, itemCount, userDuration, userStagger) {
	const targetTotal = parseInt(target.getAttribute('data-build-anim-total-duration'), 10)
	if (!isFinite(targetTotal) || targetTotal <= 0) return null
	const N = Math.max(1, itemCount)
	const expected = Math.max(1, (N - 1) * userStagger + userDuration)
	const scale = Math.max(0.05, Math.min(10, targetTotal / expected))
	return {
		targetTotal: targetTotal,
		duration: userDuration * scale,
		stagger: userStagger * scale,
	}
}

// Builds a per-item delay function that uses the text's editable Stagger
// Easing and the build-anim-scaled stagger when applicable. Falls back to
// the shared helper when no scale is provided.
function buildScaledDelayFn(baseDelay, target, totalDurationAttr, scale) {
	if (!scale) {
		return function (el, index, length) {
			return baseDelay + getEasedStaggerDelayForTransitionOutAlt(index, length, target, totalDurationAttr)
		}
	}
	const staggerEasing = getTextAnimationStaggerEasingForTarget(target)
	return function (el, index, length) {
		return baseDelay + getTextAnimationStaggerDelay(index, length, scale.stagger, staggerEasing)
	}
}

const waitForBuildAnimOriginIfNeeded = (target) => {
	if (!target || !target.hasAttribute || !target.hasAttribute('data-lay-build-anim-group')) {
		return false
	}
	if (typeof window === 'undefined' || window._layBuildAnimOriginPending !== true) {
		return false
	}
	if (target._layTextBuildAnimWaitingForOrigin) {
		return true
	}
	target._layTextBuildAnimWaitingForOrigin = true
	const retry = () => {
		if (!target || !target.isConnected) {
			return
		}
		target._layTextBuildAnimWaitingForOrigin = false
		playAnimation(target)
	}
	const onReady = () => {
		window.clearTimeout(safetyTimeout)
		retry()
	}
	window.addEventListener('lay-build-anim-origin-ready', onReady, { once: true })
	const safetyTimeout = window.setTimeout(() => {
		window.removeEventListener('lay-build-anim-origin-ready', onReady)
		retry()
	}, 3200)
	return true
}

const getBuildAnimGroupId = (target) => {
	if (!target || !target.getAttribute) {
		return ''
	}
	return target.getAttribute('data-lay-build-anim-group') || ''
}

const buildAnimGroupHasScrollMembers = (groupId) => {
	if (!groupId) {
		return false
	}
	const members = document.querySelectorAll('[data-lay-build-anim-group]')
	for (let i = 0; i < members.length; i++) {
		const member = members[i]
		if (
			member !== null &&
			member.getAttribute('data-lay-build-anim-group') === groupId &&
			member.hasAttribute('data-lay-scroll-anim')
		) {
			return true
		}
	}
	return false
}

const waitForBuildAnimGroupTriggerIfNeeded = (target) => {
	const groupId = getBuildAnimGroupId(target)
	if (!groupId) {
		return false
	}
	const shouldWaitForScrollGroup =
		target.getAttribute('data-lay-build-anim-wait-for-scroll-group') === '1' ||
		buildAnimGroupHasScrollMembers(groupId)
	if (!shouldWaitForScrollGroup) return false
	const triggeredGroups = (typeof window !== 'undefined' && window._layBuildAnimTriggeredGroups) ? window._layBuildAnimTriggeredGroups : null
	if (triggeredGroups && triggeredGroups[groupId] != null) {
		return false
	}
	const existing = animationTimelines.get(target)
	if (existing) {
		existing.pause()
		animationTimelines.delete(target)
		discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target)
		resetAnimationState(target)
		target.classList.remove('text-animation-in-view')
	}
	if (target._layTextBuildAnimWaitingForGroupTrigger) {
		return true
	}
	target._layTextBuildAnimWaitingForGroupTrigger = true
	const retry = () => {
		if (!target || !target.isConnected) {
			return
		}
		target._layTextBuildAnimWaitingForGroupTrigger = false
		playAnimation(target)
	}
	const onTriggered = (event) => {
		if (!event || !event.detail || event.detail.groupId !== groupId) {
			return
		}
		window.clearTimeout(safetyTimeout)
		window.removeEventListener('lay-build-anim-group-triggered', onTriggered)
		retry()
	}
	window.addEventListener('lay-build-anim-group-triggered', onTriggered)
	const safetyTimeout = window.setTimeout(() => {
		window.removeEventListener('lay-build-anim-group-triggered', onTriggered)
		retry()
	}, 3200)
	return true
}

// Function to create and store animation timeline
const createAnimationTimeline = (target) => {
	// const lineMasks = target.querySelectorAll('.lay-text-line-mask')
	// const dataDuration = parseFloat(target.getAttribute('data-duration')) || 500

	// Calculate base duration based on number of lines

	const animationType = target.getAttribute('data-animation-type')
	const delay = parseInt(target.getAttribute('data-delay'), 10) || 0
	const totalDuration = parseInt(target.getAttribute('data-duration'), 10) || 40
	// New (optional) timeline modifiers — all read with safe fallbacks
	// so legacy elements without these attrs render exactly as before.
	//
	//   direction   : motion direction; per-type allowlist. Used to flip
	//                 the sign of masked translates, or reverse stagger
	//                 order for left↔right reading types, or to derive
	//                 (dx,dy) for the fade_and_slide_* family.
	//   slideAmount : pixel translate distance; ONLY consumed by the
	//                 fade_and_slide_* cases.
	//   scaleFrom   : starting scale as percent (100 = no scale). When
	//                 undefined or 100 we skip the scale tween entirely
	//                 → zero runtime cost for the overwhelming majority
	//                 of texts that don't use scale.
	const directionAttrRaw = target.getAttribute('data-direction')
	const direction = (directionAttrRaw === 'left' || directionAttrRaw === 'right' || directionAttrRaw === 'up' || directionAttrRaw === 'down' || directionAttrRaw === 'bottomLeft' || directionAttrRaw === 'bottomRight') ? directionAttrRaw : null
	// `data-reading-direction` — explicit stagger order ('ltr' /
	// 'rtl'). Emitted by text.php only when set on the element; when
	// missing we fall back to the legacy direction-based heuristic in
	// `shouldReverseStaggerForType` so existing layouts continue to
	// render the same.
	const readingDirectionAttrRaw = target.getAttribute('data-reading-direction')
	const readingDirection = (readingDirectionAttrRaw === 'ltr' || readingDirectionAttrRaw === 'rtl') ? readingDirectionAttrRaw : null
	const slideAmountAttrRaw = parseFloat(target.getAttribute('data-slide-amount'))
	// Slide distance is now in EM (font-relative) so the motion
	// scales naturally with the text — a headline and a paragraph
	// both slide a proportional amount without per-breakpoint
	// tweaking. Default 0.5em ≈ half the font height feels like a
	// snappy entrance for typical headlines / paragraphs.
	const slideAmount = isFinite(slideAmountAttrRaw) && slideAmountAttrRaw >= 0 ? slideAmountAttrRaw : 0.5
	// anime.js treats raw numbers as px; pass the value with an
	// explicit `em` unit suffix so the translate is font-relative.
	const slideStart = (axis) => axis === 0 ? '0em' : (axis * slideAmount) + 'em'
	const scaleFromAttrRaw = parseFloat(target.getAttribute('data-scale-from'))
	const scaleFromActive = isFinite(scaleFromAttrRaw) && scaleFromAttrRaw !== 100
	const scaleFrom = scaleFromActive ? scaleFromAttrRaw / 100 : null
	// `data-blur-from-px` — per-unit blur tween. Mirrors scaleFrom:
	// PHP only emits the attribute when > 0, so we treat any finite
	// positive value as active and skip the filter tween entirely
	// otherwise. anime.js animates `filter` as a string tween, so
	// each timeline.add() that sets per-unit `targets` just needs
	// `cfg.filter = ['blur(<n>px)', 'blur(0px)']` to participate.
	const blurFromAttrRaw = parseFloat(target.getAttribute('data-blur-from-px'))
	const blurActive = isFinite(blurFromAttrRaw) && blurFromAttrRaw > 0
	const blurFilterFrom = blurActive ? 'blur(' + blurFromAttrRaw + 'px)' : null

	// Per-type default direction. Mirrors getTextAnimationDirectionDefault
	// in the sidebar — vertical cascades default `up`, horizontal stagger
	// reads `left`, the slide family defaults `up`.
	const directionForType = (t) => {
		if (direction) return direction
		if (t === 'fade_words' || t === 'fade_characters' || t === 'typewriter_words' || t === 'typewriter' || t === 'typewriter_fade') return 'left'
		return 'up'
	}
	// Helper: maps `data-direction` to a {dx, dy} unit vector — the
	// SIGN of the translate's starting offset (final offset is always
	// 0). `direction` follows the same source-side convention as
	// ScrollAnimation: `'left'` = element STARTS on the left side,
	// MOVES rightward; `'up'` = element MOVES up (starts below);
	// `'bottomRight'` = starts at the bottom-right corner.
	const slideVector = (t) => {
		const d = directionForType(t)
		switch (d) {
			case 'left': return { dx: -1, dy: 0 }   // starts left of zero → moves rightward
			case 'right': return { dx: 1, dy: 0 }   // starts right of zero → moves leftward
			case 'up': return { dx: 0, dy: 1 }      // starts below zero (+Y is down) → moves upward
			case 'down': return { dx: 0, dy: -1 }   // starts above zero → moves downward
			case 'bottomLeft': return { dx: -1, dy: 1 }  // starts bottom-left corner → moves to top-right
			case 'bottomRight': return { dx: 1, dy: 1 }  // starts bottom-right corner → moves to top-left
			default: return { dx: 0, dy: 1 }
		}
	}
	// Whether the inter-unit stagger order should reverse for this
	// type. Keeps things simple: only horizontal-stagger types
	// (fade_words, typewriter*, fade_and_slide_*) reverse when the
	// source side is `right` — so the wave visually starts on the
	// rightmost unit and reads toward the left. Every other type
	// (masked_*, fade_lines, etc.) keeps natural reading order;
	// direction only flips its translate.
	const shouldReverseStaggerForType = (t) => {
		// New explicit `data-reading-direction` field (sidebar UI:
		// "Reading direction LTR / RTL") wins when present — works
		// for any char / word / line cascade regardless of type. 'rtl'
		// reverses the stagger so the rightmost unit fires first
		// (Hebrew / Arabic flow).
		if (readingDirection !== null) {
			return readingDirection === 'rtl'
		}

		// Legacy fallback for elements saved before `readingDirection`
		// existed: PURE stagger types (`fade_words` / `fade_characters` /
		// `typewriter*`) used to derive stagger reversal from
		// `direction === 'right'`. Keep that behaviour so old layouts
		// render identically.
		if (t === 'typewriter_lines') {
			return directionForType(t) === 'down'
		}
		if (t === 'fade_words' || t === 'fade_characters' || t === 'typewriter_words' || t === 'typewriter' || t === 'typewriter_fade') {
			return directionForType(t) === 'right'
		}
		return false
	}
	// Wraps a `(el, idx, length) => delay` function so the `idx` passed
	// in is flipped when the type wants reversed stagger. Lets each
	// branch keep its existing delay math unaware of direction.
	const wrapDelayForDirection = (delayFn, t) => {
		if (!shouldReverseStaggerForType(t)) return delayFn
		return function (el, index, length) {
			return delayFn(el, length - 1 - index, length)
		}
	}
	// Returns `getMaskedTextRevealStartY` if direction is `up` (default),
	// or a wrapper that NEGATES the result if direction is `down` — so
	// the inner text starts ABOVE the mask (negative Y) and the reveal
	// runs top-down instead of bottom-up.
	const maskedTranslateYStart = (t) => {
		if (directionForType(t) !== 'down') return getMaskedTextRevealStartY
		return function (el) { return -getMaskedTextRevealStartY(el) }
	}
	// When the user has set an explicit `data-stagger` value, treat
	// `data-duration` as the per-sub-item animation length (how long
	// each word / char / line takes to slide in). Without the stagger
	// field, fall back to legacy behaviour where `data-duration` was
	// effectively the inter-item gap and anime.js' default per-item
	// duration (~1000ms) controlled the animation length — changing
	// this would silently restyle old pages.
	const staggerAttrRaw = parseInt(target.getAttribute('data-stagger'), 10)
	const hasExplicitStagger = isFinite(staggerAttrRaw) && staggerAttrRaw >= 0
	const perItemDuration = hasExplicitStagger ? totalDuration : undefined
	// User's per-unit duration & stagger as they'd play at 1× speed.
	// Modern (explicit stagger): duration = per-item; stagger = between-items.
	// Legacy (no stagger field): per-item duration defaults to anime.js' 1000ms;
	//   data-duration is repurposed as the inter-item gap.
	// These feed `computeBuildAnimScaling` to fit per-unit values into the
	// group's slot total at the live sub-item count.
	const userDurationForFit = hasExplicitStagger ? totalDuration : 1000
	const userStaggerForFit = hasExplicitStagger && staggerAttrRaw > 0 ? staggerAttrRaw : totalDuration
	const easing = getTextAnimationEasingForTarget(target)
	const lineMasks = target.querySelectorAll('.lay-text-line-mask')
	let timeline = anime.timeline({
		easing: easing,
		autoplay: false, // Don't start playing automatically
		complete: function () {
			if (
				animationType === 'masked_lines' ||
				animationType === 'masked_words' ||
				animationType === 'masked_characters'
			) {
				setLineTextMaskRevealClip(lineMasks, false)
			}
			// Notify subscribers (drag physics today) that this
			// trigger&play text animation finished one cycle. Drag
			// physics defers binding its physics body until this
			// signal arrives so the simulation starts from the
			// animation's end-state instead of fighting it during
			// the run. Re-fires on each replay cycle; one-shot
			// listeners handle the repeat-on-re-enter case.
			try {
				target.dispatchEvent(new CustomEvent('lay-text-anim-trigger-play-complete', { bubbles: false }))
			} catch (_) {
				// CustomEvent constructor unsupported in legacy browsers
				// — silently skip; drag physics' init path stays safe.
			}
		},
	})

	switch (animationType) {
		case 'masked_lines': {
			const lines = target.querySelectorAll('.lay-text-line')
			const scale = computeBuildAnimScaling(target, lines.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: lines,
				translateY: [maskedTranslateYStart(animationType), 0],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)

			break
		}
		case 'masked_words': {
			const characters = getWordLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				translateY: [maskedTranslateYStart(animationType), 0],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)

			break
		}
		case 'masked_characters': {
			const characters = getCharacterLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				translateY: [maskedTranslateYStart(animationType), 0],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)

			break
		}
		case 'fade_lines': {
			const lines = target.querySelectorAll('.lay-text-line')
			const scale = computeBuildAnimScaling(target, lines.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: lines,
				opacity: [0, 1],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_words': {
			const characters = getWordLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				// `wrapDelayForDirection` flips the stagger order when
				// `direction === 'right'` so the cascade reads from the
				// rightmost word inward.
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_characters': {
			const characters = getCharacterLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'typewriter_lines':
		case 'typewriter_words':
		case 'typewriter': {
			// Mixed list: laybuttons are collapsed to a single step (their bg/border
			// can't sensibly type in per-char). Inner chars of buttons stay at
			// opacity 1 (set by applyPreRevealVisualStateToTarget) so they appear
			// together with the button at its stagger position.
			const units = animationType === 'typewriter_lines'
				? Array.from(target.querySelectorAll('.lay-text-line'))
				: animationType === 'typewriter_words'
					? getWordLevelAnimationTargets(target)
					: getCharacterLevelAnimationTargets(target)
			// Stagger-only model: each char snaps to opacity 1 instantly at
			// its stagger position, no per-char animation length. Read the
			// gap from data-stagger (modern) or fall back to data-duration
			// (legacy data, before `castTextAnimation` migration runs).
			let perCharGap = hasExplicitStagger ? staggerAttrRaw : totalDuration
			// Auto-fit for build-anim groups: with no per-char duration to
			// trade off against, scaling distributes the slot total purely
			// across the gap → effective gap = targetTotal / (n - 1).
			const _twScale = computeBuildAnimScaling(target, units.length, 0, perCharGap)
			if (_twScale) perCharGap = _twScale.stagger
			const baseDelay = perCharGap
			const staggerEasing = getTextAnimationStaggerEasingForTarget(target)
			const reverseTw = shouldReverseStaggerForType(animationType)

			// first set all characters/buttons to opacity 0
			// this is needed when the text is visible on load
			anime.set(units, {
				opacity: 0,
			})
			// Hide painted text-format backgrounds before the first timeline tick.
			// Otherwise the background can flash at full size while all typewriter
			// units are already hidden and the begin callback has not run yet.
			prepareTypewriterBackgroundReveal(target, units, reverseTw)

			// The typewriter is driven by setTimeouts inside `begin`. anime.js
			// silently skipped the `begin` callback when the child was added with
			// `targets: []` (this manifested as: text visible on initial load
			// stayed at opacity 0 forever; same target after scroll-into-view
			// worked because of an unrelated re-trigger path). Pin to the real
			// target with a no-op opacity tween (current → current, duration 1)
			// so anime.js has a real animation to schedule and `begin` fires
			// reliably on the first tick after `play()`.
			const typewriterTotalMs = Math.max(1, delay + units.length * baseDelay)
			const currentOpacity = target.style.opacity !== '' ? target.style.opacity : '1'
			timeline.add({
				targets: target,
				opacity: [currentOpacity, currentOpacity],
				duration: typewriterTotalMs,
				begin: function (anim) {
					// Clear any existing timeouts for this target
					const existingTimeouts = typewriterTimeouts.get(target)
					if (existingTimeouts) {
						existingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
					}
					clearTypewriterBackgroundReveal(target)

					// first set all characters/buttons to opacity 0
					// this is needed when the text is visible on load
					anime.set(units, {
						opacity: 0,
					})

					// Store timeout IDs for cleanup
					const timeoutIds = []

					// Custom typewriter implementation without transitions.
					// When direction='right' we reverse the index so the
					// rightmost character reveals first. Note: typewriter
					// is visibility-only (no transform/scale) — direction
					// only affects which side the "wave" starts on.
					const twLen = units.length
					prepareTypewriterBackgroundReveal(target, units, reverseTw)
					units.forEach((char, index) => {
						const effIdx = reverseTw ? twLen - 1 - index : index
						const timeoutId = setTimeout(() => {
							char.style.opacity = '1'
							revealTypewriterUnitBackground(target, char)
						}, delay + getTextAnimationStaggerDelay(effIdx, twLen, baseDelay, staggerEasing))
						timeoutIds.push(timeoutId)
					})

					// Store timeout IDs for cleanup
					typewriterTimeouts.set(target, timeoutIds)
				},
			})
			break
		}
		case 'typewriter_fade': {
			const characters = getCharacterLevelAnimationTargets(target)
			// Modern: data-stagger = inter-char gap, data-duration = per-char
			// fade length. Legacy (no data-stagger): data-duration acts as
			// the gap and per-char fade falls back to anime.js' default ~1000ms.
			let perCharGap = hasExplicitStagger ? staggerAttrRaw : totalDuration
			let perCharDuration = hasExplicitStagger && totalDuration > 0 ? totalDuration : 1000
			const _twfScale = computeBuildAnimScaling(target, characters.length, perCharDuration, perCharGap)
			if (_twfScale) {
				perCharGap = _twfScale.stagger
				perCharDuration = _twfScale.duration
			}
			const reverseTwf = shouldReverseStaggerForType('typewriter_fade')
			const staggerEasing = getTextAnimationStaggerEasingForTarget(target)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				duration: perCharDuration,
				delay: function (el, index, length) {
					// Flip index when direction='right' so the rightmost
					// character starts fading first.
					const effIdx = reverseTwf ? length - 1 - index : index
					return delay + getTextAnimationStaggerDelay(effIdx, length, perCharGap, staggerEasing)
				},
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			timeline.add(cfg)
			break
		}
		case 'fade_whole_text': {
			// 1 item — auto-fit collapses to "duration = targetTotal".
			const _fwtScale = computeBuildAnimScaling(target, 1, 1000, 0)
			const durMs = parseInt(target.getAttribute('data-duration'), 10)
			const durationMs = _fwtScale
				? _fwtScale.targetTotal
				: (isFinite(durMs) && durMs > 0 ? durMs : 1000)
			const cfg = {
				targets: target,
				opacity: [0, 1],
				duration: durationMs,
				delay: delay,
			}
			// `scaleFrom` is the only motion-style knob that makes sense
			// for this one-box type — no per-unit translate / stagger.
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_whole_text': {
			// Whole-text variant of the no-mask slide family: the full
			// text block fades/slides as one unit, so no split DOM is
			// required and the stored duration maps to the whole tween.
			const _fwsScale = computeBuildAnimScaling(target, 1, 1000, 0)
			const durMs = parseInt(target.getAttribute('data-duration'), 10)
			const durationMs = _fwsScale
				? _fwsScale.targetTotal
				: (isFinite(durMs) && durMs > 0 ? durMs : 1000)
			const vec = slideVector(animationType)
			const cfg = {
				targets: target,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				duration: durationMs,
				delay: delay,
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			timeline.add(cfg)
			break
		}
		// `fade_and_slide_*` — no mask. Each unit (line/word/char)
		// fades AND translates from `(dx,dy) * slideAmount` to (0,0),
		// optionally scaling from `scaleFrom → 1`. All three diagonal
		// directions work because the runtime translates on both axes
		// independently. Stagger order reverses when direction='right'.
		case 'fade_and_slide_lines': {
			const lines = target.querySelectorAll('.lay-text-line')
			const scale = computeBuildAnimScaling(target, lines.length, userDurationForFit, userStaggerForFit)
			const vec = slideVector(animationType)
			const cfg = {
				targets: lines,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_words': {
			const characters = getWordLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const vec = slideVector(animationType)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_characters': {
			const characters = getCharacterLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const vec = slideVector(animationType)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
	}
	if (timeline) {
		// Store the timeline
		animationTimelines.set(target, timeline)
	}
	return timeline
}

// Function to play animation
const playAnimation = (target) => {
	if (isScrubTarget(target)) {
		return false
	}
	if (!animation_lifecycle_controller.isDocumentVisible()) {
		if (target && target._layTextAnimationWaitingForVisibleTab) {
			return false
		}
		if (target) {
			target._layTextAnimationWaitingForVisibleTab = true
		}
		animation_lifecycle_controller.runWhenDocumentVisible(() => {
			if (!target || !target.isConnected) {
				return
			}
			target._layTextAnimationWaitingForVisibleTab = false
			playAnimation(target)
		})
		return false
	}
	// Sync-with-Nav-Anim: defer the text-anim play until the nav-anim
	// timeline actually starts. The IO observer fires playAnimation as
	// soon as the element is in viewport + texts are split, which is
	// before `transition_navigation_in_started` — without this gate the
	// text would animate too early (before the col cascade starts).
	// The sync module flips `_laySyncFired = true` on nav-start and
	// re-invokes playAnimation, which then passes through.
	if (
		target &&
		target.hasAttribute &&
		target.hasAttribute('data-lay-sync-nav-anim') &&
		target._laySyncFired !== true
	) {
		target._laySyncWaitingForNavStart = true
		if (typeof window !== 'undefined' && window.__LAY_ANIM_DEBUG === true) {
			console.log('[lay-sync] text playAnimation DEFERRED (waiting for nav start)', {
				t: performance.now().toFixed(1),
				content: (target.textContent || '').slice(0, 30),
			})
		}
		return false
	}
	if (waitForBuildAnimGroupTriggerIfNeeded(target)) {
		return false
	}
	if (target && target.hasAttribute && target.hasAttribute('data-lay-sync-nav-anim')) {
		if (typeof window !== 'undefined' && window.__LAY_ANIM_DEBUG === true) {
			console.log('[lay-sync] text playAnimation fired', {
				t: performance.now().toFixed(1),
				delay: target.getAttribute('data-delay'),
				duration: target.getAttribute('data-duration'),
				easing: target.getAttribute('data-animation-easing'),
				content: (target.textContent || '').slice(0, 30),
			})
		}
	}
	const hadTimeline = !!animationTimelines.get(target)
	taResizeTraceTarget(target, 'playAnimation()', {
		hadTimelineBefore: hadTimeline,
		inView: target.classList.contains('text-animation-in-view'),
		readyToPlay: target.classList.contains('text-animation-ready-to-play'),
		stack: typeof Error !== 'undefined' ? new Error().stack?.split('\n').slice(1, 5).join(' ← ') : '',
	})
	taSplitLogTarget(target, 'playAnimation enter', {
		opacityBefore: target.style.opacity,
		hadTimeline,
		inViewClass: target.classList.contains('text-animation-in-view'),
	})
	const lineMasks = target.querySelectorAll('.lay-text-line-mask')
	const animationType = target.getAttribute('data-animation-type')

	// Split-DOM not ready yet: prepareTexts hasn't finished splitting the
	// text into `.lay-text-line` / `.char` / `.word` elements. If we proceed,
	// `applyPreRevealVisualStateToTarget` finds no children to set to the
	// from-state, and `addClass('lay-text-animation-initialized')` flips
	// `visibility: hidden → visible` while the raw (unanimated, unsplit)
	// text is in the DOM — that's the visible "show → animate" flicker.
	// Bail and mark for retry; the `texts_are_split` listener at the bottom
	// of this file picks pending targets up once the split completes.
	if (!isTextAnimationSplitDomReadyForPlay(target)) {
		target._layPendingPlayAfterSplit = true
		return false
	}
	if (waitForBuildAnimOriginIfNeeded(target)) {
		return false
	}

	const debugTextAnimationFlicker = typeof window !== 'undefined' && window.layTextAnimationDebug === true
	const _flickerSnapshot = (label) => {
		if (!debugTextAnimationFlicker) {
			return
		}
		const cs = window.getComputedStyle(target)
		const lineCount = target.querySelectorAll('.lay-text-line').length
		const charCount = target.querySelectorAll('.lay-text-line .char').length
		const wordCount = target.querySelectorAll('.lay-text-line .word').length
		const firstChar = target.querySelector('.lay-text-line .char')
		const childCs = firstChar ? window.getComputedStyle(firstChar) : null
		console.log('[ta-flicker]', label, animationType, (target.textContent || '').slice(0, 30), {
			t: performance.now().toFixed(1),
			visibility: cs.visibility,
			targetOpacity: cs.opacity,
			inlineOpacity: target.style.opacity,
			lineCount: lineCount,
			charCount: charCount,
			wordCount: wordCount,
			firstCharOpacity: childCs ? childCs.opacity : 'NO_CHARS',
			hasInitClass: target.classList.contains('lay-text-animation-initialized'),
		})
	}
	_flickerSnapshot('A · enter playAnimation')

	// Apply the from-state synchronously BEFORE flipping visibility on via
	// the initialized class. anime.js doesn't write the timeline's tween
	// from-values until its first rAF tick (~16ms after `play()`), so
	// without this set there's a one-frame window where the text is
	// visibility:visible AND at its end-state — produces the visible
	// "show → hide → animate in" flicker for elements that are already in
	// the viewport at page load (most common with sync-with-navigation
	// targets when there's no actual nav transition to sync to).
	//
	// Must run BEFORE the `target.style.opacity = 1` below — the helper
	// also sets `target.style.opacity = '0'` (a leftover from its main use
	// in resetAnimationState which wants the wrapper hidden during a
	// reset). For our reveal-init flow we override that back to 1 right
	// after, so the wrapper is visible while the children are at their
	// from-state (translateY:100% / opacity:0).
	applyPreRevealVisualStateToTarget(target)
	_flickerSnapshot('B · after applyPreReveal')

	// Line/word animations: show wrapper so children can fade/mask. Whole-block fade animates opacity on target.
	if (animationType !== 'fade_whole_text' && animationType !== 'fade_and_slide_whole_text') {
		target.style.opacity = 1
	}
	_flickerSnapshot('C · after target.style.opacity=1')

	// Line masks clip during play; timeline complete clears clip-path
	if (
		animationType === 'masked_lines' ||
		animationType === 'masked_words' ||
		animationType === 'masked_characters'
	) {
		setLineTextMaskRevealClip(lineMasks, true)
	}

	// Get or create timeline
	let timeline = animationTimelines.get(target)
	if (!timeline) {
		timeline = createAnimationTimeline(target)
	}
	_flickerSnapshot('D · after createAnimationTimeline')

	// Add initialized class when animation starts
	target.classList.add('lay-text-animation-initialized')
	_flickerSnapshot('E · after addClass(initialized) — visibility flips to visible')

	// Play the timeline
	target.classList.remove('text-animation-never-played')
	target.classList.remove('text-animation-ready-to-play')
	target.removeAttribute('data-text-animation-ready-from')
	target.classList.add('text-animation-in-view')
	timeline.play()
	_flickerSnapshot('F · after timeline.play() — anime.js first-tick has NOT run yet')
	requestAnimationFrame(() => {
		_flickerSnapshot('G · 1 rAF later — anime.js should have written first-tick state by now')
		requestAnimationFrame(() => {
			_flickerSnapshot('H · 2 rAFs later')
		})
	})
	return true
}

// Fullpage.js translates the section / slide wrapper instead of scrolling,
// so IO may not fire reliably. CSS scroll-snap has a related edge case:
// bottom-aligned targets can rest below their configured data-top trigger
// line and only cross it during browser-specific overscroll. Both modes emit
// the fp lifecycle events below, so use those as the browser-stable fallback.
const taFpFindContainer = (sectionIndex, slideIndex) => {
	const sections = document.querySelectorAll('.fp-section')
	const section = sections[sectionIndex]
	if (!section) return null
	if (typeof slideIndex === 'number' && slideIndex >= 0) {
		const slides = section.querySelectorAll('.fp-slide')
		return slides[slideIndex] || section
	}
	const activeSlide = section.querySelector('.fp-slide.active')
	return activeSlide || section
}

const taFpResolveEventContainer = (info, sectionIndex, slideIndex) => {
	const passedSection = info && info.section
	const section = passedSection && typeof passedSection.contains === 'function'
		? passedSection
		: passedSection && passedSection[0] && typeof passedSection[0].contains === 'function'
			? passedSection[0]
			: null

	if (section) {
		// Scroll-snap passes the actual snap target (`.row`) for both axes.
		// Fullpage horizontal mode passes its `.fp-section`, so resolve the
		// active `.fp-slide` by index in that one case.
		const needsFullpageSlide =
			typeof slideIndex === 'number' &&
			slideIndex >= 0 &&
			section.classList &&
			section.classList.contains('fp-section')
		if (!needsFullpageSlide) {
			return section
		}
	}

	return taFpFindContainer(sectionIndex, slideIndex)
}

const taFpPlay = (container) => {
	if (!container) {
		return
	}
	currentTargets.forEach((target) => {
		if (!container.contains(target)) return
		// Scrub targets normally have their progress driven by `scrub.js`
		// from real scroll position, but fp doesn't scroll — so we play
		// scrub text-animations as a one-shot timeline here too. The
		// timeline factory in `playAnimation` doesn't care whether the
		// target was originally scrub-configured; it builds the same
		// per-tween anime.js timeline either way and `play()` runs it
		// through to completion.
		if (!isTextAnimationSplitDomReadyForPlay(target)) {
			target._layPendingPlayAfterSplit = true
			return
		}
		if (waitForBuildAnimGroupTriggerIfNeeded(target)) {
			return
		}
		// Drop the existing timeline so playAnimation builds a fresh one
		// from the current pre-reveal state — matches the "replay on arrival"
		// semantics the user wants per slide/section visit.
		const existing = animationTimelines.get(target)
		if (existing) {
			resetAnimationState(target)
			animationTimelines.delete(target)
		}
		playAnimation(target)
	})
}

const taFpReset = (container) => {
	if (!container) return
	currentTargets.forEach((target) => {
		if (!container.contains(target)) return
		// Roll the timeline back to pre-reveal so re-entering the slide /
		// section replays the animation from its starting frame. Scrub
		// targets included — same one-shot replay semantics for both.
		resetAnimationState(target)
		animationTimelines.delete(target)
		target.classList.remove('text-animation-in-view')
		target.classList.add('text-animation-ready-to-play')
	})
}

// Reset previously-active section/slide only AFTER the transition
// completes — i.e. on `afterLoad`/`afterSlideLoad`, not on `onLeave`. fp
// fires `onLeave` at the START of its slide animation, while the leaving
// slide is still partially or fully visible; resetting text animations
// then would visibly snap them back to pre-reveal state in front of the
// user. By the time `afterLoad`/`afterSlideLoad` fires, the leaving
// slide is fully off-screen, so the reset is invisible.
let taFpPrevSectionIndex = null
let taFpPrevSlideIndex = null
let taFpPrevSlideSectionIndex = null
let taFpPrevSectionContainer = null
let taFpPrevSlideContainer = null

const taFpDetectInitialSlide = (sectionIndex) => {
	const sections = document.querySelectorAll('.fp-section')
	const section = sections[sectionIndex]
	if (!section) return null
	const activeSlide = section.querySelector('.fp-slide.active')
	if (!activeSlide) return null
	const slides = section.querySelectorAll('.fp-slide')
	return Array.prototype.indexOf.call(slides, activeSlide)
}

jQuery(window).on('fpAfterLoad', (event, info) => {
	if (!info || typeof info.index !== 'number') return
	const newIdx = info.index - 1
	const newContainer = taFpResolveEventContainer(info, newIdx)
	if (taFpPrevSectionIndex !== null && taFpPrevSectionIndex !== newIdx) {
		const prevSection = taFpPrevSectionContainer || document.querySelectorAll('.fp-section')[taFpPrevSectionIndex]
		taFpReset(prevSection)
	}
	taFpPlay(newContainer)
	taFpPrevSectionIndex = newIdx
	taFpPrevSectionContainer = newContainer
	taFpPrevSlideIndex = taFpDetectInitialSlide(newIdx)
	taFpPrevSlideSectionIndex = taFpPrevSlideIndex !== null ? newIdx : null
	taFpPrevSlideContainer = null
})

jQuery(window).on('fpAfterSlideLoad', (event, info) => {
	if (!info || typeof info.index !== 'number' || typeof info.slideIndex !== 'number') return
	const sectionIdx = info.index - 1
	const newSlideIdx = info.slideIndex
	const newContainer = taFpResolveEventContainer(info, sectionIdx, newSlideIdx)
	if (
		taFpPrevSlideIndex !== null &&
		taFpPrevSlideSectionIndex === sectionIdx &&
		taFpPrevSlideIndex !== newSlideIdx
	) {
		taFpReset(taFpPrevSlideContainer || taFpFindContainer(sectionIdx, taFpPrevSlideIndex))
	}
	taFpPlay(newContainer)
	taFpPrevSlideIndex = newSlideIdx
	taFpPrevSlideSectionIndex = sectionIdx
	taFpPrevSlideContainer = newContainer
})

jQuery(window).on('fpAfterRender', () => {
	const initialContainer = taFpFindContainer(0)
	taFpPlay(initialContainer)
	taFpPrevSectionIndex = 0
	taFpPrevSectionContainer = initialContainer
	taFpPrevSlideIndex = taFpDetectInitialSlide(0)
	taFpPrevSlideSectionIndex = taFpPrevSlideIndex !== null ? 0 : null
	taFpPrevSlideContainer = taFpPrevSlideIndex !== null ? taFpFindContainer(0, taFpPrevSlideIndex) : null
})

// Per-scope state. Direct-load fires `content_ready` twice in quick
// succession (once for the overlay's project content, once for the lazy-
// loaded underlay), and the previous single-global registry was getting
// overwritten by the second fire — the first scope's IO observers were
// torn down and never re-created, leaving its text-animations stuck at
// visibility:hidden / opacity:0.
//
// Track targets and observer-cleanups separately per scope. `currentTargets`
// remains the UNION exposed via `getAllScopeTargets()` for the legitimate
// union iterators (resize handlers, IO resume, fp lifecycle, NAV_SYNC),
// while scope-specific code reads `currentTargetsByScope[scope]` directly
// via `getTargetsForScope(scope)` or accepts an explicit `targets` argument.
// Cleanup is scoped — content_ready for one scope only disconnects that
// scope's observers, leaving the other scope intact.
let currentTargetsByScope = { normal: [], overlay: [] }
let currentTaObserversByScope = { normal: null, overlay: null }
const recomputeCurrentTargets = function () {
	currentTargets = currentTargetsByScope.normal.concat(currentTargetsByScope.overlay)
}

// Initialize text splitting
// Sync-with-Nav-Anim hook. The sync controller calls this to re-fire
// playAnimation on a sync target once `transition_navigation_in_started`
// fires — i.e. the moment the nav-anim timeline is about to play. The
// initial playAnimation call from the IO observer was bailed (because
// `_laySyncFired === false`); this re-entry sets the gate open and calls
// playAnimation again, which now passes through with the stored
// `data-delay` honoured by anime.js so the text intro runs at the right
// cascade slot.
window.layReplayDeferredTextAnimation = function (target) {
	if (!target) return
	target._laySyncFired = true
	target._laySyncWaitingForNavStart = false
	playAnimation(target)
}

window.laytheme.on('content_ready', function (typeOfContentThatIsReady, $target, isProjectOverlayObj) {
	const isProjectOverlay = isProjectOverlayObj != undefined && isProjectOverlayObj.isProjectOverlay === true
	const scope = isProjectOverlay ? 'overlay' : 'normal'

	var parentSelector = isProjectOverlay ? '#lay-project-overlay>.lay-content' : 'body>.lay-content'
	column_selectors = undefined
	switch (typeOfContentThatIsReady) {
		case 'desktop':
			column_selectors = [parentSelector + ' #grid', parentSelector + ' .lay-fixed-rows-layer-desktop', parentSelector + ' .cover-region-desktop .cover-inner', parentSelector + ' #footer']
			break
		case 'phone':
			var footerHasPhoneCPL = jQuery(parentSelector + ' #footer-custom-phone-grid').length != 0
			var hasCPL = jQuery(parentSelector + ' #custom-phone-grid').length != 0

			if (footerHasPhoneCPL && hasCPL) {
				column_selectors = [parentSelector + ' #custom-phone-grid', parentSelector + ' .lay-fixed-rows-layer-phone', parentSelector + ' .lay-fixed-rows-layer-desktop', parentSelector + ' .cover-region-phone .cover-inner', parentSelector + ' #footer-custom-phone-grid']
			} else if (!footerHasPhoneCPL && hasCPL) {
				column_selectors = [parentSelector + ' #custom-phone-grid', parentSelector + ' .lay-fixed-rows-layer-phone', parentSelector + ' .lay-fixed-rows-layer-desktop', parentSelector + ' .cover-region-phone .cover-inner', parentSelector + ' #footer']
			} else if (footerHasPhoneCPL && !hasCPL) {
				column_selectors = [parentSelector + ' #grid', parentSelector + ' .lay-fixed-rows-layer-desktop', parentSelector + ' .cover-region-desktop .cover-inner', parentSelector + ' #footer-custom-phone-grid']
			} else if (!footerHasPhoneCPL && !hasCPL) {
				column_selectors = [parentSelector + ' #grid', parentSelector + ' .lay-fixed-rows-layer-desktop', parentSelector + ' .cover-region-desktop .cover-inner', parentSelector + ' #footer']
			}
			break
	}

	if (Array.isArray(column_selectors) && column_selectors != undefined) {
		for (let index = 0; index < column_selectors.length; index++) {
			column_selectors[index] = column_selectors[index] + ' ' + selector
		}
	}

	// Initialize text splitting for all matching elements. Page overlays live in
	// the page DOM too, but they have their own text-animation lifecycle; if the
	// normal page observer touches them before the overlay opens, they can be
	// left in their finished state and appear instead of animating in.
	const newTargetsForScope = Array.from(document.querySelectorAll(column_selectors)).filter((target) => {
		if (isProjectOverlay) {
			return true
		}
		return !(target.closest && target.closest('.lay-overlay'))
	})

	// Idempotency guard — if this scope's targets and the existing
	// observer state both already match `newTargetsForScope`, this is
	// a redundant content_ready fire (lay-theme emits content_ready
	// multiple times during a single nav cycle: prepareTexts retry,
	// lazy-image-load reflow, etc.). Skip the full prepareTexts +
	// initObserver rebuild — re-running it would unobserve every
	// element and re-observe them, briefly clearing the IO state in
	// the middle of an active animation cycle.
	const prevTargets = currentTargetsByScope[scope] || []
	const hasObserver = !!currentTaObserversByScope[scope]
	const sameTargets = hasObserver
		&& prevTargets.length === newTargetsForScope.length
		&& prevTargets.every((t, i) => t === newTargetsForScope[i])
	if (sameTargets) {
		taSplitLog('content_ready: same scope+targets, no-op', { scope: scope, targets: newTargetsForScope.length })
		return
	}

	// Clean up only THIS scope's existing observers (preserve the
	// other scope's). The legacy `window.currentTaObservers` global is
	// kept in sync below for any external code still reading it.
	if (currentTaObserversByScope[scope]) {
		currentTaObserversByScope[scope]()
		currentTaObserversByScope[scope] = null
	}

	currentTargetsByScope[scope] = newTargetsForScope
	recomputeCurrentTargets() // currentTargets is now the union of both scopes
	if (newTargetsForScope.length > 0) {
		// Pass the per-scope target list explicitly so storeOriginalTexts and
		// prepareTexts touch ONLY this scope's elements. The direct-load flow
		// fires content_ready twice (overlay + lazy-loaded underlay); without
		// scoping, the second call would iterate the union and re-trigger
		// visual resets / replays on the first scope's already-playing
		// animations (text_15 plays twice, scrub texts flicker).
		storeOriginalTexts(newTargetsForScope)
		taSplitLog('content_ready: prepareTexts → …', { textAnimationTargets: newTargetsForScope.length, scope: scope, unionCount: currentTargets.length })
		prepareTexts(true, newTargetsForScope)
			.then(() => {
				taSplitLog('content_ready: prepareTexts settled → initObserver', { scope: scope })
				const cleanup = initObserver(newTargetsForScope)
				currentTaObserversByScope[scope] = cleanup
				window.currentTaObservers = cleanup // legacy alias
			})
			.catch((err) => {
				const cleanup = initObserver(newTargetsForScope)
				currentTaObserversByScope[scope] = cleanup
				window.currentTaObservers = cleanup
			})
	}
})

/**
 * Returns the UNION of all scopes' text-animation targets (overlay +
 * normal). Rename hint: this is intentionally union-scoped — most callers
 * should be working with one scope's targets at a time, in which case use
 * `getTargetsForScope(scope)` or accept a `targets` argument explicitly.
 *
 * Legitimate union callers:
 *   - resize handlers (any visible target on the page can need a resplit)
 *   - the iOS scroll resume nudge
 *   - fullpage.js play/reset (filtered later by `container.contains`)
 *   - the `texts_are_split` listener (filtered later by mutation-set)
 *   - NAV_SYNC_START_EVENT handler (filtered later by per-target sync timing)
 *
 * If you're writing new code that processes targets and aren't sure if
 * union iteration is correct, ask yourself: "would the second
 * content_ready (underlay scope) emit cause this to mis-touch overlay
 * state?" If yes — that's a Phase 2 violation; take an explicit scope or
 * targets argument.
 */
var getAllScopeTargets = function () {
	if (!Array.isArray(currentTargets)) {
		currentTargets = [currentTargets]
	}
	return currentTargets
}

/** Returns the per-scope target list. Use this in scope-specific code. */
var getTargetsForScope = function (scope) {
	const list = currentTargetsByScope[scope]
	return Array.isArray(list) ? list : []
}

// Back-compat alias for any callers (including internal) still using the
// old, ambiguous name. New code should use `getAllScopeTargets`.
const getCurrentTargets = getAllScopeTargets

export { getAllScopeTargets, getTargetsForScope, getCurrentTargets, createAnimationTimeline, animationTimelines, typewriterTimeouts }
